class BuyTogetherLookCard extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.intervalId = null;
    this.slides = [];
    this.storiesContainer = null;
    this.stories = [];

    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.mediaQuery = window.matchMedia("(min-width: 990px)");

    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
  }

  connectedCallback() {
    if (this.dataset.buyTogetherCardInitialized === "true") {
      return;
    }

    this.dataset.buyTogetherCardInitialized = "true";

    this.updateActiveDeviceElements();

    this.addEventListener("mouseenter", this.handleMouseEnter);

    this.addEventListener("mouseleave", this.handleMouseLeave);

    this.addEventListener("focusin", this.handleFocusIn);

    this.addEventListener("focusout", this.handleFocusOut);

    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    this.mediaQuery.addEventListener("change", this.handleBreakpointChange);

    this.prefersReducedMotion.addEventListener("change", this.handleReducedMotionChange);

    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();

    this.removeEventListener("mouseenter", this.handleMouseEnter);

    this.removeEventListener("mouseleave", this.handleMouseLeave);

    this.removeEventListener("focusin", this.handleFocusIn);

    this.removeEventListener("focusout", this.handleFocusOut);

    document.removeEventListener("visibilitychange", this.handleVisibilityChange);

    this.mediaQuery.removeEventListener("change", this.handleBreakpointChange);

    this.prefersReducedMotion.removeEventListener("change", this.handleReducedMotionChange);

    delete this.dataset.buyTogetherCardInitialized;
  }

  get autoplayDelay() {
    const seconds = Number(this.dataset.autoplaySeconds || 5);

    if (!Number.isFinite(seconds) || seconds <= 0) {
      return 0;
    }

    return Math.max(seconds, 2) * 1000;
  }

  getDevice() {
    return this.mediaQuery.matches ? "desktop" : "mobile";
  }

  getSlides(device = this.getDevice()) {
    return Array.from(
      this.querySelectorAll(`[data-buy-together-card-slide][data-buy-together-card-device="${device}"]`),
    );
  }

  getStoriesContainer(device = this.getDevice()) {
    return this.querySelector(`[data-buy-together-card-stories][data-buy-together-card-device="${device}"]`);
  }

  getStories(device = this.getDevice()) {
    return Array.from(
      this.querySelectorAll(
        `[data-buy-together-card-stories][data-buy-together-card-device="${device}"] [data-buy-together-card-story]`,
      ),
    );
  }

  updateActiveDeviceElements() {
    const activeDevice = this.getDevice();
    const inactiveDevice = activeDevice === "desktop" ? "mobile" : "desktop";

    this.slides = this.getSlides(activeDevice);

    this.storiesContainer = this.getStoriesContainer(activeDevice);

    this.stories = this.getStories(activeDevice);

    this.resetInactiveDevice(inactiveDevice);

    this.currentIndex = 0;

    if (this.slides.length > 0) {
      this.showSlide(0);
    }

    this.updateStoriesVisibility();
  }

  resetInactiveDevice(device) {
    const inactiveSlides = this.getSlides(device);

    const inactiveStoriesContainer = this.getStoriesContainer(device);

    const inactiveStories = this.getStories(device);

    inactiveSlides.forEach((slide, index) => {
      const isFirst = index === 0;

      slide.classList.toggle("is-active", isFirst);

      slide.setAttribute("aria-hidden", String(!isFirst));
    });

    inactiveStoriesContainer?.classList.remove("is-visible");

    inactiveStories.forEach((story) => {
      story.classList.remove("is-active", "is-complete");
    });
  }

  updateStoriesVisibility() {
    if (!this.storiesContainer) {
      return;
    }

    const shouldShow = this.slides.length > 1 && this.autoplayDelay > 0 && !this.prefersReducedMotion.matches;

    this.storiesContainer.classList.toggle("is-visible", shouldShow);
  }

  updateStories(activeIndex) {
    this.stories.forEach((story, index) => {
      const progress = story.querySelector(".buy-together-collection-card__story-progress");

      story.classList.remove("is-active", "is-complete");

      if (progress) {
        progress.style.animation = "none";
      }

      if (index < activeIndex) {
        story.classList.add("is-complete");
        return;
      }

      if (index !== activeIndex) {
        return;
      }

      story.style.setProperty("--buy-together-autoplay-duration", `${this.autoplayDelay}ms`);

      story.classList.add("is-active");

      if (progress) {
        void progress.offsetWidth;
        progress.style.animation = "";
      }
    });
  }

  pauseStories() {
    this.stories.forEach((story) => {
      const progress = story.querySelector(".buy-together-collection-card__story-progress");

      if (!progress) {
        return;
      }

      const computedTransform = window.getComputedStyle(progress).transform;

      progress.style.animation = "none";
      progress.style.transform = computedTransform;
    });
  }

  handleMouseEnter() {
    this.stopAutoplay();
  }

  handleMouseLeave() {
    this.startAutoplay();
  }

  handleFocusIn() {
    this.stopAutoplay();
  }

  handleFocusOut(event) {
    if (!this.contains(event.relatedTarget)) {
      this.startAutoplay();
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopAutoplay();
      return;
    }

    this.startAutoplay();
  }

  handleBreakpointChange() {
    this.stopAutoplay();
    this.updateActiveDeviceElements();
    this.startAutoplay();
  }

  handleReducedMotionChange() {
    this.updateStoriesVisibility();

    if (this.prefersReducedMotion.matches) {
      this.stopAutoplay();
      return;
    }

    this.startAutoplay();
  }

  startAutoplay() {
    this.clearAutoplay();

    if (this.slides.length <= 1 || !this.autoplayDelay || this.prefersReducedMotion.matches || document.hidden) {
      return;
    }

    this.updateStories(this.currentIndex);

    this.intervalId = window.setInterval(() => {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;

      this.showSlide(nextIndex);
    }, this.autoplayDelay);
  }

  clearAutoplay() {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);

      this.intervalId = null;
    }
  }

  stopAutoplay() {
    this.clearAutoplay();
    this.pauseStories();
  }

  showSlide(index) {
    if (!this.slides[index]) {
      return;
    }

    this.currentIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;

      slide.classList.toggle("is-active", isActive);

      slide.setAttribute("aria-hidden", String(!isActive));
    });

    this.updateStories(index);
  }
}

if (!customElements.get("buy-together-look-card")) {
  customElements.define("buy-together-look-card", BuyTogetherLookCard);
}
